/**
 * The RULES behind a "this selection, or all of them?" pre-flight dialog.
 *
 * Extracted from `PreCompareConfirmation`, which is where all of this was
 * first argued out, so that Add to Thrive's own dialog inherits the reasoning
 * instead of re-deriving it. Only the rules move: the two dialogs keep their
 * own copy, their own caps and their own layout, because their numbers mean
 * different things (Compare counts rows it has not looked at yet; Add counts
 * rows it is about to write).
 *
 * Pure and synchronous on purpose — every rule here is a claim about sets, and
 * a claim about sets is testable without a DOM.
 */

/** Which arm of the dialog a run takes. */
export type ScopeChoice = "selection" | "all";

export interface ScopeDecision {
  /** The two arms would act on the SAME rows, so there is nothing to choose. */
  choiceIsNotAChoice: boolean;
  /** Collapsed, AND the selection is over the explicit-lane cap. */
  collapsedIsOverCap: boolean;
  /** The arm the button will actually run. */
  effectiveChoice: ScopeChoice;
  /** The chosen arm names more ids than the explicit lane can carry. */
  selectionTooLarge: boolean;
}

export function resolveScopeChoice({
  hasSelection,
  countsAreExact,
  countsAreComparable,
  selectedCount,
  selectedPayloadCount,
  allCount,
  cap,
  choice,
}: {
  /** Is there an active selection at all? `false` → a single "all" statement. */
  hasSelection: boolean;
  /**
   * Is `allCount` EXACT yet?
   *
   * Load-bearing for the collapse. Both dialogs show an approximate whole-set
   * number first (from an aggregate the page already holds) and replace it when
   * the exact count lands. Collapsing on the approximation could equate a
   * 15-row selection with a 12-row run — two different sets, one number.
   */
  countsAreExact: boolean;
  /**
   * Are `selectedCount` and `allCount` produced by the SAME predicate over the
   * same universe?
   *
   * The collapse below reasons "equal counts imply equal sets", and that step
   * is only valid when the two numbers are measuring the same thing. Compare's
   * are: both count *pending* rows, one intersected with the selection. Add's
   * are NOT — its selected count is computed client-side over the loaded page
   * (counting unloaded ids as writable, and counting conflicts the recruiter
   * has decided), while its all-count is the server's, which excludes every
   * conflict regardless of decision. Equal numbers there can mean different
   * sets, so Add passes `false` and never collapses.
   *
   * REQUIRED, with no default. It was optional-defaulting-to-`true`, so a new
   * dialog that never thought about the question inherited the collapse — and
   * the collapse is the unsafe direction: it hides a real choice behind a
   * statement and runs an arm the recruiter did not pick. A premise the caller
   * must assert out loud costs one line at each of the two call sites and
   * cannot be inherited by accident (/pr-review iter 3, Agent C N5).
   */
  countsAreComparable: boolean;
  /** |selection ∩ actionable| — what the dialog PROMISES for this arm. */
  selectedCount: number;
  /**
   * How many ids the selection arm would actually PUT ON THE WIRE.
   *
   * Separate from `selectedCount`, and the separation is the whole point: the
   * cap is a limit on the REQUEST, not on the promise. Add subtracts undecided
   * conflicts from what it promises but still sends them (they carry the
   * decision), so 210 selected with 15 conflicts promises 195 and posts 210 —
   * and a guard reading the promise waves through a body the server refuses
   * with a 422, or the edge refuses with a bare 403 before it is ever seen.
   *
   * Compare's two numbers coincide (it filters the id list it sends), which is
   * why this went unnoticed when the rules were extracted from it.
   */
  selectedPayloadCount: number;
  /** |actionable| over the whole scope. */
  allCount: number;
  /** The explicit lane's id cap. The `all` arm has none — it sends no ids. */
  cap: number;
  /** What the radios currently say. Ignored when the choice collapses. */
  choice: ScopeChoice;
}): ScopeDecision {
  /*
   * EQUAL COUNTS IMPLY EQUAL SETS, and that is a proof rather than a
   * coincidence worth checking: `selectedCount` is |selection ∩ actionable| and
   * `allCount` is |actionable|, so the two are equal exactly when the selection
   * CONTAINS the actionable set — and then the intersection IS that set. There
   * is no shape where the numbers agree and the rows differ.
   */
  const choiceIsNotAChoice =
    hasSelection && countsAreExact && countsAreComparable && selectedCount === allCount;

  /*
   * THE COLLAPSED CASE TAKES THE `all` ARM WHEN THE SELECTION IS OVER THE CAP,
   * and this is the whole reason the over-cap guard is not a dead end. The
   * collapse fires when the two arms act on identical rows and the collapsed
   * layout renders no radios — so forcing `"selection"` there would block the
   * run and point at a control that is not on screen: the copy would offer "run
   * all of them instead" with no way to do it, and only Cancel would work.
   *
   * It is also the likeliest route into the guard: ticking every page of a
   * 300-contact import is exactly a selection that contains the actionable set.
   * Three reviewers found this independently on the Compare dialog, whose first
   * version was tested only with `selectedCount !== allCount` — a case that
   * never enters the collapse at all.
   *
   * One arm is capped and the other is not, and they do the same thing. Taking
   * the uncapped one is not a workaround; it is the only reading of a choice
   * that was never a choice.
   */
  const collapsedIsOverCap = choiceIsNotAChoice && selectedPayloadCount > cap;

  const effectiveChoice: ScopeChoice = choiceIsNotAChoice
    ? collapsedIsOverCap
      ? "all"
      : "selection"
    : hasSelection
      ? choice
      : "all";

  /*
   * An explicit selection over the cap cannot be sent, and the recruiter should
   * learn that HERE rather than from a 422 after the fact — the alternative is
   * one radio away and has no limit at all, which is the part a later error
   * message would not convey.
   *
   * Only the `selection` arm is capped. And by the time this reads
   * `effectiveChoice` the collapsed case has already moved itself to `all`, so
   * it never fires there.
   */
  const selectionTooLarge = effectiveChoice === "selection" && selectedPayloadCount > cap;

  return { choiceIsNotAChoice, collapsedIsOverCap, effectiveChoice, selectionTooLarge };
}
